import { countParam } from '@/pages/catalogImport/notices';
import type { ImportWork } from './importWork';

/**
 * The work in words: what replacing the file loses (`lose`), what changing
 * the branch keeps (`keep`), and the file card's short form (`counts`).
 * Every form names only the counts above zero, each with its plural form,
 * so a sentence never says "0 rows".
 */
export type WorkWording = 'lose' | 'keep' | 'counts';

/**
 * One count in words, for the View to translate: `t(key, params)`, where
 * `count` picks the plural form and `rows` or `names` is the count as
 * written (`countParam`).
 */
export type WorkPart = {
    key: string;
    params: Record<string, string | number>;
};

/** The word a `lose` sentence joins its last part with: "and" / "და". */
export const WORK_AND_KEY = 'catalogImport.work.and';

/** Each form's key per count, in the order every form names them. */
const PART_KEYS = {
    lose: {
        editedRows: 'catalogImport.work.lose.editedRows',
        excludedRows: 'catalogImport.work.lose.excludedRows',
        changedNames: 'catalogImport.work.lose.changedNames',
    },
    keep: {
        editedRows: 'catalogImport.work.keep.editedRows',
        excludedRows: 'catalogImport.work.keep.excludedRows',
        changedNames: 'catalogImport.work.keep.changedNames',
    },
    counts: {
        editedRows: 'catalogImport.work.counts.editedRows',
        excludedRows: 'catalogImport.work.counts.excludedRows',
        changedNames: 'catalogImport.work.counts.changedNames',
    },
} as const satisfies Record<WorkWording, Record<keyof ImportWork, string>>;

/** The interpolation each count writes itself as. */
const COUNT_PARAMS = {
    editedRows: 'rows',
    excludedRows: 'rows',
    changedNames: 'names',
} as const satisfies Record<keyof ImportWork, string>;

const COUNT_ORDER = [
    'editedRows',
    'excludedRows',
    'changedNames',
] as const satisfies readonly (keyof ImportWork)[];

/**
 * The counts above zero as messages of the form (`pages.catalogImport.work.<wording>.*`),
 * in order: what the View translates and hands to `workSentenceOf` or
 * `workCountsLine`. None without work.
 */
export const workPartsOf = (
    work: ImportWork,
    wording: WorkWording,
): WorkPart[] =>
    COUNT_ORDER.filter((count) => work[count] > 0).map((count) => ({
        key: PART_KEYS[wording][count],
        params: {
            count: work[count],
            [COUNT_PARAMS[count]]: countParam(work[count]),
        },
    }));

/**
 * The translated parts as part of a sentence:
 * - `lose` (Replace the file), with `and` (`WORK_AND_KEY` translated): "your
 *   changes to 3 rows, 1 excluded row and 2 name choices" / "3 სტრიქონის
 *   ცვლილება, 1 გამორიცხული სტრიქონი და 2 სახელის არჩევანი", the last part
 *   joined by it;
 * - `keep` (Change the branch), without: "3 edited rows, 1 excluded, 2 name
 *   choices" / "3 შესწორებული, 1 გამორიცხული, 2 სახელის არჩევანი",
 *   comma-joined.
 *
 * Empty without parts: a dialog that names the work opens only with some.
 */
export const workSentenceOf = (
    parts: readonly string[],
    and?: string,
): string => {
    if (and === undefined || parts.length < 2) {
        return parts.join(', ');
    }
    const last = parts[parts.length - 1];
    return `${parts.slice(0, -1).join(', ')} ${and} ${last}`;
};

/** A part's own spaces as no-break spaces: a line wraps between parts, never inside one. */
const unbroken = (part: string): string => part.replace(/ /g, '\u00a0');

/**
 * The file card's short form of the translated `counts` parts, " · "-joined:
 * "3 edited · 1 excluded · 2 name choices" / "3 შესწორებული · 1
 * გამორიცხული · 2 სახელის არჩევანი". On a narrow card the line wraps only
 * between parts, so a count never leaves its word. Empty without parts.
 */
export const workCountsLine = (parts: readonly string[]): string =>
    parts.map(unbroken).join(' · ');
